/**
 * Hidden testing-mode flag for the chat assistant.
 *
 * When enabled, chat, Snabbsök and rating requests carry the `x-im-testing: 1`
 * header (the Snabbsök click beacon carries `testing: true` in its body, since
 * sendBeacon cannot set headers). It is only a log flag: the server marks the
 * row as test traffic - `testing` in chat_answers, snabbsok_impressions,
 * snabbsok_clicks and chat_ratings - so staff queries can be filtered out of
 * real-user numbers. The answer itself is unaffected.
 *
 * Activation:
 *   - Local dev (localhost / 127.0.0.1 / 0.0.0.0): always on, no opt-in
 *     needed. Local queries are always staff and shouldn't pollute
 *     real-user analytics, so the flag short-circuits to true regardless
 *     of URL param or localStorage.
 *   - Deployed environments: visit any app URL with `?testing=1` to enable
 *     for this browser; visit with `?testing=0` to disable. State persists
 *     in localStorage so it survives reloads and navigation.
 *
 * Visual feedback: when active, a small "TEST MODE" badge renders fixed
 * top-right so the tester can't forget they're polluting test data.
 */

const STORAGE_KEY = 'imchat:testing'
const URL_PARAM = 'testing'
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0'])

/**
 * True when the page is being served from a local dev server. SSR-safe.
 */
function isLocalEnvironment(): boolean {
	if (typeof window === 'undefined') return false
	return LOCAL_HOSTNAMES.has(window.location.hostname)
}

/**
 * Read URL param on page load and update localStorage. Called from
 * entry.client.tsx so it runs once per page load before React mounts.
 *
 * The URL param is consumed (removed from the URL) so the address bar
 * stays clean and the flag's source of truth becomes localStorage.
 *
 * Note: in local dev the param still updates localStorage as documented,
 * but `isTestingMode()` ignores localStorage there — local is always on.
 */
export function syncTestingFlagFromUrl(): void {
	if (typeof window === 'undefined') return

	// This runs pre-hydration in entry.client.tsx, so ANY throw here breaks the
	// whole app (hydration never runs). The entire body is a best-effort no-op
	// nicety - persist the flag, tidy the URL - with two browser-API throw-sites
	// in restricted contexts: window.localStorage (SecurityError when storage is
	// blocked) and window.history.replaceState (SecurityError in an opaque-origin
	// sandboxed iframe). Neither failure has a recovery worth differentiating, so
	// one guard makes the function provably throw-proof: on any failure we do
	// nothing. isTestingMode() reads the flag fresh and is independently guarded,
	// so a skipped persist just means the flag isn't remembered in this browser.
	try {
		const url = new URL(window.location.href)
		const param = url.searchParams.get(URL_PARAM)
		if (param === null) return

		if (param === '1' || param === 'true') {
			window.localStorage.setItem(STORAGE_KEY, '1')
		} else if (param === '0' || param === 'false') {
			window.localStorage.removeItem(STORAGE_KEY)
		}

		url.searchParams.delete(URL_PARAM)
		window.history.replaceState({}, '', url.toString())
	} catch {
		// blocked storage or an opaque-origin sandbox - leave things as-is
	}
}

/**
 * Check whether testing mode is active in the current browser. SSR-safe:
 * returns false on the server. Always true in local dev so that
 * development queries are marked as test traffic in the AI answer log.
 */
export function isTestingMode(): boolean {
	if (typeof window === 'undefined') return false
	if (isLocalEnvironment()) return true
	// localStorage access throws (SecurityError) when storage is blocked - a
	// sandboxed embed iframe, Safari cross-site-tracking prevention, or blocked
	// third-party cookies. The flag is a no-op nicety, so treat a throw as off
	// rather than letting it bubble to the error boundary and break the chat.
	try {
		return window.localStorage.getItem(STORAGE_KEY) === '1'
	} catch {
		return false
	}
}
